import { useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { Navigate, Outlet } from "react-router";
import { Spinner, Button } from "@repo/ui";
import { routes } from "~/lib/constants";
import { SidebarNavigation } from "../side-bar/side-bar";

export const ProtectedRoute = () => {
  const auth = useAuth();
  const hasAttemptedBootRefresh = useRef(false);
  const [isBootRefreshing, setIsBootRefreshing] = useState(false);
  const [isNetworkTimeout, setIsNetworkTimeout] = useState(false);

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && auth.user && !hasAttemptedBootRefresh.current) {
      hasAttemptedBootRefresh.current = true;
      setIsBootRefreshing(true);

      const attemptBootRefresh = async () => {
        try {
          // Set an aggressive timeout
          // If VPN is off, we don't want to wait 50 seconds for the browser to give up.
          const timeout = new Promise((_, reject) =>
            setTimeout(() => reject(new Error("NETWORK_TIMEOUT")), 5000)
          );

          await Promise.race([auth.signinSilent(), timeout]);
          setIsBootRefreshing(false);
        } catch (error: any) {
          console.warn("Boot refresh failed:", error);

          if (error.message === "NETWORK_TIMEOUT" || error.message.includes("fetch")) {
            setIsNetworkTimeout(true);
          } else {
            void auth.removeUser();
          }
          setIsBootRefreshing(false);
        }
      };

      void attemptBootRefresh();
    }
  }, [auth]);

  if (isNetworkTimeout) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 p-6 text-center">
        <h2 className="text-xl font-bold">Cannot connect to Auth Server</h2>
        <p className="text-muted-foreground max-w-md">
          We couldn&apos;t reach the auth server. Please ensure you are connected to the **VPN** and try
          again.
        </p>
        <Button onClick={() => window.location.reload()}>Retry Connection</Button>
      </div>
    );
  }

  const isSilentlyRefreshing = auth.activeNavigator === "signinSilent" || isBootRefreshing;
  const isRestoringSession =
    auth.isLoading ||
    isSilentlyRefreshing ||
    (auth.user && !auth.isAuthenticated && !hasAttemptedBootRefresh.current);

  if (isRestoringSession) {
    return (
      <div className="flex items-center gap-2 p-10">
        <Spinner />
        <span>Restoring session...</span>
      </div>
    );
  }

  return auth.isAuthenticated ? (
    <div className="flex min-h-screen">
      <SidebarNavigation />
      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
    </div>
  ) : (
    <Navigate to={routes.login} replace />
  );
};
